import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Drawer } from '@hud/web-components/Drawer/Drawer';
import { Button } from '@hud/web-components/Button/Button';
import { Toggle } from '@hud/web-components/Toggle/Toggle';
import { Config, SchemaItem, Value } from '@hud/rules-dtos/types';
import {
  FooterActions,
  ParametersContainer,
  ResetDefaultButton,
  Separator,
  StatusContainer,
  StyledButton,
} from './EditRuleDrawer.styles';
import {
  DetectionParameter,
  SupportedDescriptors,
} from '@hud/web-components/Rules/types';
import { GetAllServicesResponse } from '@hud/portal-dtos/webapp-service.dto';
import { NumberDetectionParameter } from '@hud/web-components/Rules/RuleTypes/NumberDetectionParameter';
import TagsMultiSelect from '@hud/web-components/MultiSelect/TagsMultiSelect/TagsMultiSelect';
import { ConfirmationPopover } from '@hud/web-components/Popover/ConfirmationPopover';
import {
  NotificationType,
  showNotification,
} from '../../../components/ToastNotification/ToastNotification';
import useAnalytics from '../../../hooks/useAnalytics';

export interface EditRuleDrawerProps {
  opened: boolean;
  onClose: () => void;
  initialEnabled: boolean;
  schema: SchemaItem[];
  descriptors: SupportedDescriptors;
  initialConfig: Config;
  mode: 'detection' | 'service';
  services?: GetAllServicesResponse['services'];
  initialServiceIds?: number[];
  alreadyConfiguredServices?: number[];
  onSave: (
    enabled: boolean,
    config: Config,
    serviceIds?: number[],
  ) => Promise<void>;
  onDelete?: () => Promise<void>; // Only used in service mode
  ruleId: string;
  ruleConfigId?: number;
}

export function EditRuleDrawer({
  opened,
  onClose,
  initialEnabled,
  schema,
  descriptors,
  initialConfig,
  onSave,
  onDelete,
  mode,
  services = [],
  initialServiceIds = [],
  alreadyConfiguredServices = [],
  ruleId,
}: EditRuleDrawerProps) {  
  const analytics = useAnalytics();
  const [enabled, setEnabled] = useState(initialEnabled);
  const [config, setConfig] = useState<Config>(initialConfig || {});
  const [serviceIds, setServiceIds] = useState<number[]>(initialServiceIds);
  const [isSaving, setIsSaving] = useState(false);
  const prevOpenedRef = useRef(opened);

  useEffect(() => {
    if (opened && !prevOpenedRef.current) {
      setEnabled(initialEnabled);
      setConfig(initialConfig || {});
      setServiceIds(initialServiceIds);
    }
    prevOpenedRef.current = opened;
  }, [opened, initialEnabled, initialConfig, initialServiceIds]);

  const handleClose = () => {
    onClose();
  };

  const handleResetToDefaults = () => {
    const defaultConfig = schema.reduce(
      (acc, item) => {
        acc[item.name] = item.defaultValue as Value;
        return acc;
      },
      {} as Record<string, Value>,
    );
    setConfig({ ...defaultConfig });
    analytics.capture('rule_edit_config_reset_to_defaults_clicked', {
      rule_id: ruleId,
      rule_edit_config_mode: mode,
    });
  };

  const isServiceMode = mode === 'service';
  const title = isServiceMode
    ? initialServiceIds.length > 0
      ? 'Edit Service Configuration'
      : 'Add Service Configuration'
    : 'Edit Detection Logic';
  const testIdPrefix = isServiceMode
    ? 'add-service-config'
    : 'edit-detection-logic';

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Calculate which config values have changed
      const updatedConfig: Config = {};

      Object.keys(config).forEach((key) => {
        if (config[key] !== initialConfig[key]) {
          updatedConfig[key] = config[key];
        }
      });

      if (isServiceMode) {
        await onSave(enabled, updatedConfig, serviceIds);
      } else {
        await onSave(enabled, updatedConfig);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save rule config', error);
      showNotification({
        type: NotificationType.ERROR,
        message: 'There was an error saving the configuration',
      });
    } finally {
      showNotification({
        type: NotificationType.SUCCESS,
        message: 'Successfully saved the configuration',
      });
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (onDelete) {
        await onDelete();
      }
      onClose();
    } catch (error) {
      console.error('Failed to delete rule config', error);
      showNotification({
        type: NotificationType.ERROR,
        message: 'There was an error deleting the configuration',
      });
    } finally {
      showNotification({
        type: NotificationType.SUCCESS,
        message: 'Successfully deleted configuration',
      });
    }
  };

  const handleConfigChange = (detectionParam: DetectionParameter) => {
    setConfig({
      ...config,
      [detectionParam.id]: detectionParam.value,
    });
  };

  const isFormSubmittable = useMemo(() => {
    // Check if config values have changed
    const hasConfigChanges = schema.some((item) => {
      const currentValue = config?.[item.name];
      const initialValue = initialConfig?.[item.name] ?? item.defaultValue;
      return currentValue !== undefined && currentValue !== initialValue;
    });

    // Check if all values are defined
    const allValuesDefined = Object.values(config).every(
      (value) => value !== undefined && value !== null && value !== '',
    );

    if (!allValuesDefined) {
      return false;
    }

    // Check if enabled state has changed
    const hasEnabledChanged = enabled !== initialEnabled;

    // For service mode:
    // - If there are no initial services, require service selection and config changes or enabled state change
    // - If there are initial services, require either service selection or config changes
    if (isServiceMode) {
      // Check if service IDs have changed (only in service mode)
      const hasServiceIdsChanged =
        isServiceMode &&
        (serviceIds.length !== initialServiceIds.length ||
          serviceIds.some((id) => !initialServiceIds.includes(id)));

      if (initialServiceIds.length === 0) {
        return hasServiceIdsChanged && (hasConfigChanges || hasEnabledChanged);
      }
      return hasServiceIdsChanged || hasConfigChanges || hasEnabledChanged;
    }

    // For detection logic mode, check if there are any config changes or enabled state change and all values are defined
    return hasConfigChanges || hasEnabledChanged;
  }, [
    schema,
    initialConfig,
    config,
    enabled,
    initialEnabled,
    isServiceMode,
    serviceIds,
    initialServiceIds,
  ]);

  const drawerDescriptors = Object.fromEntries(
    Object.entries(descriptors).map(([key, value]) => [
      key,
      { ...value, tooltip: value.recommendation },
    ]),
  );

  const isDirtyParam = (item: SchemaItem) => {
    const currentValue = config?.[item.name];
    const initialValue = initialConfig?.[item.name] ?? item.defaultValue;
    return currentValue !== undefined && currentValue !== initialValue;
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      title={title}
      icon={'other.plug'}
      role="region"
      aria-label={
        isServiceMode ? 'add service configuration' : 'edit detection logic'
      }
      size={450}
      footer={
        <FooterActions>
          <div className="left-actions">
            {isServiceMode && initialServiceIds.length > 0 && (
              <ConfirmationPopover
                text="Are you sure you want to delete this custom configuration?"
                confirmationLabel="Yes"
                onSubmit={handleDelete}
              >
                <StyledButton
                  className="delete"
                  variant="outlined"
                  data-testid={`${testIdPrefix}-delete-button`}
                >
                  Delete
                </StyledButton>
              </ConfirmationPopover>
            )}
          </div>

          <div className="right-actions">
            <Button
              variant="outlined"
              onClick={handleClose}
              data-testid={`${testIdPrefix}-cancel-button`}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <ConfirmationPopover
              text={
                isServiceMode
                  ? "Warning: This change will affect the selected services for this rule's detection. Are you sure you want to proceed?"
                  : "Warning: This change will affect the entire account for this rule's detection. Are you sure you want to proceed?"
              }
              confirmationLabel="Yes"
              onSubmit={handleSave}
            >
              <StyledButton
                data-testid={`${testIdPrefix}-save-button`}
                disabled={
                  isSaving ||
                  (isServiceMode && serviceIds.length === 0) ||
                  !isFormSubmittable
                }
                variant="glow"
              >
                Save
              </StyledButton>
            </ConfirmationPopover>
          </div>
        </FooterActions>
      }
    >
      <StatusContainer>
        <span>Enabled</span>
        <Toggle
          value={enabled}
          onToggle={() => setEnabled(!enabled)}
          label="Enabled"
        />
      </StatusContainer>

      {isServiceMode && (
        <>
          <ServiceSelector
            services={services}
            selectedServices={serviceIds}
            onServicesChange={setServiceIds}
            disabledServices={alreadyConfiguredServices}
          />
          <Separator />
        </>
      )}

      {schema.length > 0 && (
        <>
          <ParametersContainer>
            {schema.map((item) => {
              return (
                <NumberDetectionParameter
                  key={item.name}
                  role="listitem"
                  detectionParameter={{
                    id: item.name,
                    value: (config[item.name] === undefined // null is a valid value
                      ? item.defaultValue
                      : config[item.name]) as number,
                    operator: 'gte',
                  }}
                  readonly={item.readOnly}
                  descriptors={drawerDescriptors}
                  onParameterChange={handleConfigChange}
                  fullWidth={true}
                  dirty={isDirtyParam(item)}
                />
              );
            })}
          </ParametersContainer>

          <ResetDefaultButton variant="text" onClick={handleResetToDefaults}>
            Reset to Hud recommendations
          </ResetDefaultButton>
        </>
      )}
    </Drawer>
  );
}

const ServiceSelector = ({
  services,
  selectedServices,
  onServicesChange,
  disabledServices = [],
}: {
  services: GetAllServicesResponse['services'];
  selectedServices: number[];
  onServicesChange: (serviceIds: number[]) => void;
  disabledServices?: number[];
}) => {
  return (
    <TagsMultiSelect
      placeholder="Select service"
      options={services.map((service) => ({
        label: service.name,
        value: service.id.toString(),
        icon: 'entities.servicesBlank',
      }))}
      selectedValues={selectedServices.map((id) => id.toString())}
      disabledOptions={disabledServices.map((id) => id.toString())}
      onChange={(selectedOptions) => {
        onServicesChange(selectedOptions.map((id) => parseInt(id)));
      }}
    />
  );
};
